import { Link } from 'react-router-dom';
import { PLANS, formatPlanPrice } from '@sarnia/shared';
import PublicLayout from '../../components/layout/PublicLayout';
import { Icon } from '../../components/ui/Icon';
import type { IconName } from '../../components/ui/Icon';
import './help.css';

const basicPrice = formatPlanPrice(PLANS[0]!);
const premiumPrice = formatPlanPrice(PLANS[1]!);

interface HelpSection {
  id: string;
  icon: IconName;
  title: string;
  paragraphs: string[];
  tip: string;
}

const sections: HelpSection[] = [
  {
    id: 'create',
    icon: 'file-plus',
    title: 'Create a blank site',
    paragraphs: [
      'Start from scratch when you want to build exactly the page you have in mind. In your dashboard choose New site, then select the Blank page template under Start from scratch.',
      'Your blank site opens in the block editor with a simple header and footer already in place. Everything else is added by you, block by block.'
    ],
    tip: 'The blank template includes the guided tour. If you close it, you can reopen it by adding ?tour=1 to the editor URL.'
  },
  {
    id: 'blocks',
    icon: 'layout',
    title: 'Add blocks',
    paragraphs: [
      'Blocks are the building blocks of your page. Use header and footer for navigation and contact details, hero for a strong opening, text for flexible copy, and image & text to pair visuals with a message.',
      'Feature grid works well for services or benefits, gallery for photos, pricing for clear offers, testimonials for social proof, menu list and opening hours for cafés and restaurants, and contact plus map address to help people find you.',
      'Use FAQ to answer common questions, call to action to drive the next step, social links for your profiles, and spacer to control the rhythm of the page.'
    ],
    tip: 'Start with a header, a hero and a contact section. You can add the rest as the page takes shape.'
  },
  {
    id: 'edit',
    icon: 'edit',
    title: 'Edit content',
    paragraphs: [
      'Click any text directly on the canvas to edit it, or select a block and use the Inspector panel on the right. The Inspector shows every editable field for the selected block.',
      'You can change headings, body copy, buttons, prices, social links and images without touching code. Use the link button in the text toolbar to make any selection clickable.'
    ],
    tip: 'Highlight a word, then paste a link to turn it into a link. The Inspector also shows the controls for each item in a list or grid.'
  },
  {
    id: 'arrange',
    icon: 'move',
    title: 'Arrange the page',
    paragraphs: [
      'Open the Structure tab on the left to see every block on the page in order. Click a block to select it, then use the up and down arrows to move it.',
      'You can duplicate any block to reuse a layout, or delete it to remove it completely. Undo and redo are always available in the toolbar.'
    ],
    tip: 'Move blocks to create a natural story: opener first, proof in the middle, call to action near the end.'
  },
  {
    id: 'theme',
    icon: 'palette',
    title: 'Make it yours',
    paragraphs: [
      'Open the Theme tab to change the colours, fonts, corner rounding and section spacing across the whole site. Pick a calm pairing that matches your business.',
      'The editor works in both light and dark mode. Use the theme toggle in the top navigation to switch the editor without affecting your published site.'
    ],
    tip: 'Use the accent colour sparingly for buttons and important links, and keep body text high contrast against the background.'
  },
  {
    id: 'media',
    icon: 'image',
    title: 'Add images',
    paragraphs: [
      'Open the Media tab to upload images from your device. Once uploaded, you can select an image for any block that supports it.',
      'Add short, descriptive alt text for every image. Good alt text helps visitors using screen readers and improves accessibility.'
    ],
    tip: 'Use images at roughly the size they will appear. The builder keeps them sharp and fast across devices.'
  },
  {
    id: 'devices',
    icon: 'monitor',
    title: 'Preview on every device',
    paragraphs: [
      'Use the device switcher in the editor toolbar to preview desktop, tablet and phone widths. Click each device to see how the layout responds.',
      'Check that headings wrap neatly, buttons stay tappable and images do not overflow their containers.'
    ],
    tip: 'Most visitors on the island will look at your site on a phone, so check the phone view before publishing.'
  },
  {
    id: 'publish',
    icon: 'rocket',
    title: 'Share, export or go live',
    paragraphs: [
      'When you are ready, open the Share tab to create a shareable preview link. It is clearly marked as a preview and kept out of search engines, so it is perfect for feedback—not a website people should rely on.',
      'Prefer to host it yourself? Download your site as a ZIP. You will get plain HTML, CSS, images and a README with hosting steps. Upload the files to any web host.',
      `Want help going live? On Basic (${basicPrice}/mo) our team gets your site onto your own domain and keeps it running. Premium (${premiumPrice}/mo) adds priority setup and more hands-on care.`
    ],
    tip: 'A preview address can be 3–40 characters using lowercase letters, numbers and hyphens. You can stop sharing at any time.'
  }
];

const faqs = [
  {
    question: 'How long does it take to build a site from scratch?',
    answer: 'Most blank-page sites go from empty canvas to a shareable preview in under an hour. The guided tour covers the essentials in about two minutes.'
  },
  {
    question: 'Do I need to know how to code?',
    answer: 'No. The block editor and theme controls handle the code for you. You click, type and upload.'
  },
  {
    question: 'Can I start with a template instead?',
    answer: 'Yes. The Template Gallery includes fully designed kits. You can still edit every block, colour and image.'
  },
  {
    question: 'Can I update my site after sharing a preview?',
    answer: 'Yes. Edit any time, then press Share or Update preview. If you have already downloaded a ZIP, export again to get the latest files.'
  },
  {
    question: 'What happens if I close the guided tour?',
    answer: 'It simply closes. You can restart it by adding ?tour=1 to the editor URL.'
  },
  {
    question: 'Is the blank template free to use?',
    answer: 'The blank template is available with your Sarnia Builder workspace. Every block works without extra design setup.'
  }
];

export function GettingStartedPage() {
  return (
    <PublicLayout>
      <section className="help-hero">
        <div className="sarnia-container">
          <p className="help-eyebrow">Getting started</p>
          <h1>Build your first website from scratch</h1>
          <p className="help-lead">
            Follow a friendly guided tour and turn an empty canvas into a clear, confident
            Guernsey website—block by block, without code.
          </p>
          <div className="help-hero__actions">
            <Link className="sarnia-btn sarnia-btn--primary help-hero__action" to="/dashboard/sites/new?template=blank">
              Start the interactive tutorial
            </Link>
            <Link className="sarnia-btn sarnia-btn--secondary help-hero__action help-hero__action--secondary" to="/templates">
              Browse templates
            </Link>
          </div>
        </div>
      </section>

      <section className="help-layout sarnia-container">
        <aside className="help-toc" aria-label="Table of contents">
          <p className="help-toc__title">On this page</p>
          <nav>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>
                {section.title}
              </a>
            ))}
            <a href="#designed">Prefer a designed template?</a>
            <a href="#faq">FAQ</a>
          </nav>
        </aside>

        <div className="help-content">
          <ol className="help-steps">
            {sections.map((section, index) => (
              <li key={section.id} id={section.id} className="help-card">
                <span className="help-card__index">0{index + 1}</span>
                <div className="help-card__icon" aria-hidden="true">
                  <Icon name={section.icon} size={22} />
                </div>
                <h2>{section.title}</h2>
                {section.paragraphs.map((paragraph) => (
                  <p key={paragraph}>{paragraph}</p>
                ))}
                <p className="help-tip">
                  <span className="help-tip__label">Pro tip</span> {section.tip}
                </p>
              </li>
            ))}
          </ol>

          <section id="designed" className="help-card help-designed">
            <div className="help-card__icon" aria-hidden="true">
              <Icon name="sparkles" size={22} />
            </div>
            <h2>Prefer a designed template?</h2>
            <p>
              Browse the Template Gallery to start from a finished design. Click any text to edit
              it, replace images, change colours and fonts, toggle effects, and show, hide or
              reorder whole sections—then make it yours.
            </p>
            <Link className="help-inline-link" to="/templates">
              Browse templates
            </Link>
          </section>

          <section id="faq" className="help-faq">
            <h2>Frequently asked questions</h2>
            {faqs.map((faq) => (
              <details key={faq.question} className="help-faq__item">
                <summary>{faq.question}</summary>
                <p>{faq.answer}</p>
              </details>
            ))}
          </section>

          <div className="help-cta">
            <h2>It only takes a few minutes</h2>
            <p>Start with a blank page, follow the tour and share or export something you are proud of.</p>
            <Link className="sarnia-btn sarnia-btn--primary" to="/dashboard/sites/new?template=blank">
              Create a blank site
            </Link>
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
